import {forwardRef, useContext, useEffect, useImperativeHandle, useRef} from "react";
import {FetchContext} from "../context/FetchContext";
import {LocalAudioTrack, LocalVideoTrack, RoomEvent} from "livekit-client";
import {Int16ToUint8Array, kNumSignedUrlsPerBatch, SendEOSForLocalRecording, TimeKeeper} from "../utils/utils";
import {getFailedChunksForAsset, retryFailedChunks, saveFailedChunk} from "../IndexedDB";
import ModernChunkedMediaRecorder from "./ModernChunkedMediaRecorder";

const SendTimingRequest = ({room}) => {
    const clientTime = performance.now();
    const buffer = new ArrayBuffer(8);
    const view = new DataView(buffer);
    view.setFloat64(0, clientTime, true); // little-endian
    const uint8Data = new Uint8Array(buffer);

    room.localParticipant.publishData(uint8Data, {
        reliable: true,
        destinationIdentities: ['TimerAgent']
    });
};

const SendSignedUrlsRequest = ({room, signedUrlsBatchNumberRef}) => {
    const buffer = new ArrayBuffer(8);
    const view = new DataView(buffer);
    view.setInt32(0, signedUrlsBatchNumberRef.current, true); // little-endian
    signedUrlsBatchNumberRef.current += 1;
    const uint8Data = new Uint8Array(buffer);

    room.localParticipant.publishData(uint8Data, {
        reliable: true,
        destinationIdentities: ['RecordingAgent']
    });
};

const prepareBlobForUpload = (videoDataBlob, currentChunkNumberRef) => {

    const chunkNumber = currentChunkNumberRef.current;
    currentChunkNumberRef.current += 1;

    return {chunkNumber, videoDataBlob: videoDataBlob};
};

const uploadBlobToSignedUrl = async ({
                                         chunkNumber,
                                         videoDataBlob,
                                         assetIdRef,
                                         signedUrlListRef,
                                         room,
                                         totalUploadedBytesRef,
                                         totalRecordedBytesRef,
                                         setUploadProgress,
                                         failedChunkUploadRef,
                                         metadataParsedRef,
                                     }) => {
    if (!metadataParsedRef.current && signedUrlListRef.current.length === 0) {
        const metadata = room.localParticipant.metadata;
        try {
            const parsed = JSON.parse(metadata);
            metadataParsedRef.current = true;
            const urls = parsed.urls;
            const batchNumber = parsed.batchNumber;
            const startIndex = batchNumber * kNumSignedUrlsPerBatch;
            for (let i = 0; i < urls.length; i++) {
                signedUrlListRef.current[startIndex + i] = urls[i];
            }
            // console.log("Received signed urls from recording agent");
            // const debugList = signedUrlListRef.current.map((url) => url.split("?")[0].slice(-8));
            // console.log("Signed urls are ", debugList);
        } catch (e) {
            console.error("Error parsing metadata to get signed urls for continous uploading", e);
        }
    }

    const signedUrl = signedUrlListRef.current.length > chunkNumber
        ? signedUrlListRef.current[chunkNumber]
        : null;

    if (!signedUrl) {
        console.warn(`No signed URL for chunk #${chunkNumber}`);
        return;
    }

    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            await fetch(signedUrl, {
                method: "PUT",
                body: videoDataBlob,
            });

            const suffix = signedUrl.split("?")[0].slice(-8);
            console.log(`Chunk #${chunkNumber} uploaded to ${suffix} (attempt ${attempt})`);

            totalUploadedBytesRef.current += videoDataBlob.size;
            if (totalRecordedBytesRef.current > 0) {
                setUploadProgress(
                    (totalUploadedBytesRef.current / totalRecordedBytesRef.current) * 100
                );
            }

            videoDataBlob = null; // Clear the blob reference
            return; // ✅ success
        } catch (e) {
            console.warn(`Upload attempt ${attempt} failed for chunk #${chunkNumber}`, e);
            if (attempt < maxAttempts) {
                await new Promise(res => setTimeout(res, 100 * attempt)); // backoff: 100ms, 200ms
            } else {
                console.error(`Upload failed permanently for chunk #${chunkNumber}`);
                try {
                    await saveFailedChunk(assetIdRef.current, signedUrl, {
                        data: videoDataBlob,
                        signedUrl,
                    });
                    console.log("Saved failed chunk to IndexedDB for retry later");
                    failedChunkUploadRef.current = true;
                } catch (err) {
                    console.error("Error saving failed chunk to IndexedDB", err);
                }
                videoDataBlob = null; // Clear the blob reference
                return;
            }
        }
    }

    videoDataBlob = null; // Clear the blob reference
};

const ConsumeData = async (videoDataBlob, assetIdRef, client, currentChunkNumberRef, signedUrlListRef, room, totalUploadedBytesRef, totalRecordedBytesRef, setUploadProgress, failedChunkUploadRef, metadataParsedRef) => {

    const {chunkNumber} = prepareBlobForUpload(videoDataBlob, currentChunkNumberRef);
    if (videoDataBlob === null) {
        console.error("Blob is null after preparing for upload");
        return;
    }

    uploadBlobToSignedUrl({
        chunkNumber,
        videoDataBlob,
        assetIdRef,
        signedUrlListRef,
        room,
        totalUploadedBytesRef,
        totalRecordedBytesRef,
        setUploadProgress,
        failedChunkUploadRef,
        metadataParsedRef
    }).catch((e => {
        console.error("Error uploading chunk", e);
    }));
};

const decoder = new TextDecoder();

const median = (arr) => {
    const mid = Math.floor(arr.length / 2);
    const nums = [...arr].sort((a, b) => a - b);
    return arr.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
};


const HandleDataReceived = (payload, participant, timeKeeperRef, pongsRef, signedUrlListRef) => {
    //console.log("Received data from: ", participant.identity)

    const payloadString = decoder.decode(payload);
    const payloadObject = JSON.parse(payloadString);

    if (participant.identity === 'RecordingAgent') {
        // The object will have a field called urls which is a list. We want to append this to the signedUrlListRef.
        const urls = payloadObject.urls;
        signedUrlListRef.current.push(...urls);
        return;
    }

    try {
        // Decode binary Uint8Array to string if needed
        const clientTime = payloadObject.clientTimestamp;
        const serverTime = payloadObject.serverTimestamp;

        const timeReceived = performance.now();

        pongsRef.current.push(timeReceived - clientTime);
        if (pongsRef.current.length > 5) pongsRef.current.shift();

        const latency = median(pongsRef.current);
        const remoteTime = serverTime + latency / 2;

        timeKeeperRef.current.setStartTime(remoteTime);

    } catch (err) {
        console.error("Error decoding time sync message:", err);
    }
};

const ModernLocalVideoRecording = forwardRef(function ModernLocalVideoRecording({
                                                                                    livekitToken,
                                                                                    assetIdRef,
                                                                                    localRecordingEnabled,
                                                                                    room,
                                                                                    setRecordingDone,
                                                                                    setUploadProgress,
                                                                                    setLocalRecordingStopped,
                                                                                    client,
                                                                                    isHDQuality,
                                                                                    is4KQuality,
                                                                                    shouldRetryFailedChunks,
                                                                                }, ref) {

            const fetchContext = useContext(FetchContext);
            const retryIntervalRef = useRef(null);

            const currentChunkNumberRef = useRef(0);

            const timeKeeperRef = useRef(new TimeKeeper());

            const pongsRef = useRef([]);

            const signedUrlListRef = useRef([]);
            const signedUrlsBatchNumberRef = useRef(1);

            const recordingStartTimeRef = useRef(0);

            const totalRecordedBytesRef = useRef(0);
            const totalUploadedBytesRef = useRef(0);

            const maybeStartRetryLoop = (assetId, onDone) => {
                if (retryIntervalRef.current) return;

                retryIntervalRef.current = setInterval(async () => {
                    const remaining = await getFailedChunksForAsset(assetId);

                    if (remaining.length === 0) {
                        clearInterval(retryIntervalRef.current);
                        retryIntervalRef.current = null;
                        console.log("✅ All chunks uploaded for asset", assetId);
                        onDone?.(); // Call your finalize or notifyServer function
                        return;
                    }

                    await retryFailedChunks(remaining);
                }, 5000); // every 5 seconds
            };

            const audioContextRef = useRef(null);
            const mixedDestinationRef = useRef(null);
            const localTrackConnectedRef = useRef(false);
            const isRecordingRef = useRef(false);
            const recorderRef = useRef(null);
            const consumeIntervalRef = useRef(null);
            const timingRequestsInterval = useRef(null);
            const signedUrlsRequestInterval = useRef(null);
            const failedChunkUploadRef = useRef(false);
            const metadataParsedRef = useRef(false);

            const videoDataRef = useRef([]);
            const isUploadingRef = useRef(false);


            useEffect(() => {
                if (!localRecordingEnabled) {
                    return;
                }

                const newAudioContext = new AudioContext({sampleRate: 48000, latencyHint: "playback"});
                const newMixedDestination = newAudioContext.createMediaStreamDestination();
                audioContextRef.current = newAudioContext;
                mixedDestinationRef.current = newMixedDestination;

                return () => {
                    if (newAudioContext !== null && newAudioContext.state !== "closed") {
                        newAudioContext.close();
                    }
                };
            }, [localRecordingEnabled]);

            useEffect(() => {
                if (room === null || audioContextRef.current === null || mixedDestinationRef.current === null || !localRecordingEnabled) {
                    return;
                }

                const ConnectTrack = async (trackPublication) => {
                    if (trackPublication.track instanceof LocalAudioTrack) {
                        const audioTrack = trackPublication.audioTrack;
                        const sourceNode = audioContextRef.current.createMediaStreamSource(new MediaStream([audioTrack.mediaStreamTrack]));
                        sourceNode.connect(mixedDestinationRef.current);
                        localTrackConnectedRef.current = true;
                    }
                };

                room.on(RoomEvent.LocalTrackPublished, ConnectTrack);
            }, [room, localRecordingEnabled]);

            useEffect(() => {
                if (room && room.localParticipant) {
                    room.on(RoomEvent.DataReceived, (payload, participant, kind) => HandleDataReceived(payload, participant, timeKeeperRef, pongsRef, signedUrlListRef));

                    timingRequestsInterval.current = setInterval(() => {
                        SendTimingRequest({room});
                    }, 500);

                    signedUrlsRequestInterval.current = setInterval(() => {
                        SendSignedUrlsRequest({room, signedUrlsBatchNumberRef});
                    }, 1000 * 60 * 5); // every 5 minutes

                    return () => {
                        room.off(RoomEvent.DataReceived, HandleDataReceived);
                        clearInterval(timingRequestsInterval.current);
                        clearInterval(signedUrlsRequestInterval.current);
                    };
                }
            }, [room]);

            useEffect(() => {
                return () => {
                    if (retryIntervalRef.current) {
                        clearInterval(retryIntervalRef.current);
                    }
                };
            }, []);

            const maybeConsumeNextChunk = async () => {
                if (isUploadingRef.current) return;
                if (videoDataRef.current.length === 0) return;

                isUploadingRef.current = true;

                let nextBlob = videoDataRef.current.shift();
                try {
                    await ConsumeData(
                        nextBlob,
                        assetIdRef,
                        client,
                        currentChunkNumberRef,
                        signedUrlListRef,
                        room,
                        totalUploadedBytesRef,
                        totalRecordedBytesRef,
                        setUploadProgress,
                        failedChunkUploadRef,
                        metadataParsedRef
                    );
                } finally {

                    // Forcefully release blob and GC
                    try {
                        if (nextBlob && typeof nextBlob.arrayBuffer === 'function') {
                            await nextBlob.arrayBuffer();  // force decode
                        }
                    } catch (_) {
                    }

                    nextBlob = null;
                    isUploadingRef.current = false;
                    setTimeout(() => {
                        maybeConsumeNextChunk(); // slight delay allows GC to kick in
                    }, 10);
                }
            };

            const startRecording = async () => {

                clearInterval(timingRequestsInterval.current);

                if (isRecordingRef.current) {
                    console.log("Start recording called but already recording");
                    return;
                }

                if (!localRecordingEnabled) {
                    console.log("Local recording not enabled");
                    isRecordingRef.current = true; // Set to true to prevent multiple calls
                    return;
                }

                if (!localTrackConnectedRef.current && room.localParticipant.audioTrackPublications.size !== 0) {
                    const firstKey = room.localParticipant.audioTrackPublications.keys().next().value;
                    const audioTrack = room.localParticipant.audioTrackPublications.get(firstKey).audioTrack;
                    const sourceNode = audioContextRef.current.createMediaStreamSource(new MediaStream([audioTrack.mediaStreamTrack]));
                    sourceNode.connect(mixedDestinationRef.current);
                    localTrackConnectedRef.current = true;
                }

                const audioContext = audioContextRef.current;
                const mixedDestination = mixedDestinationRef.current;

                const audioTrack = new LocalAudioTrack(mixedDestination.stream.getAudioTracks()[0]);

                let mediaStream;
                if (room.localParticipant.videoTrackPublications.size === 0) {
                    mediaStream = new MediaStream([audioTrack.mediaStreamTrack]);
                } else {
                    const firstKey = room.localParticipant.videoTrackPublications.keys().next().value;
                    const videoTrack = room.localParticipant.videoTrackPublications.get(firstKey).videoTrack;
                    mediaStream = new MediaStream([videoTrack.mediaStreamTrack, audioTrack.mediaStreamTrack]);
                }

                recorderRef.current = new ModernChunkedMediaRecorder(mediaStream, 90000);
                recordingStartTimeRef.current = timeKeeperRef.current.getCurrentTime();
                recorderRef.current.start((blob) => {
                    videoDataRef.current.push(blob);
                    maybeConsumeNextChunk(); // triggers upload loop
                }, isHDQuality, is4KQuality);

                const ReplaceVideoTrack = async (trackPublication) => {
                    if (trackPublication.track instanceof LocalVideoTrack) {
                        const videoStreamTrack = trackPublication.videoTrack.mediaStreamTrack;
                        if (recorderRef.current) {
                            console.log("Calling replace video track");
                            recorderRef.current.replaceVideoTrack(videoStreamTrack);
                        }
                    }
                }

                room.on(RoomEvent.LocalTrackPublished, ReplaceVideoTrack);
                room.on(RoomEvent.TrackUnmuted, ReplaceVideoTrack);

                isRecordingRef.current = true;
            }

            const stopRecording = async () => {
                if (!isRecordingRef.current) {
                    return;
                }

                clearInterval(timingRequestsInterval.current);
                clearInterval(signedUrlsRequestInterval.current);

                if (!localRecordingEnabled) {
                    setLocalRecordingStopped(true);
                    setTimeout(() => {
                        setRecordingDone(true);
                    }, 1000);
                    recorderRef.current = null;
                    isRecordingRef.current = false;
                    return;
                }

                const waitForFinalChunk = () =>
                    new Promise((resolve) => {
                        recorderRef.current.stop(resolve); // pass a callback to run on final chunk ready
                    });

                if (recorderRef.current) {
                    await waitForFinalChunk();
                    recorderRef.current = null;
                }

                isRecordingRef.current = false;
                setLocalRecordingStopped(true);

                await maybeConsumeNextChunk();
                videoDataRef.current = [];

                // Stop audio context
                if (audioContextRef.current && audioContextRef.current.state !== "closed") {
                    audioContextRef.current.close();
                    audioContextRef.current = null;
                }

                if (failedChunkUploadRef.current) {
                    maybeStartRetryLoop(assetIdRef.current, () => {
                        // Wait a second before sending EOS to make sure chunks are uploaded and can be found
                        // on storage buckets.
                        setTimeout(() => {
                            SendEOSForLocalRecording({
                                assetId: assetIdRef.current,
                                fetchContext,
                                livekitToken,
                                chunkNumber: currentChunkNumberRef.current,
                            }).then(() => {
                                setRecordingDone(true);
                            });
                        }, 1000);
                    });
                } else {
                    setTimeout(() => {
                        SendEOSForLocalRecording({
                            assetId: assetIdRef.current,
                            fetchContext,
                            livekitToken,
                            chunkNumber: currentChunkNumberRef.current,
                        }).then(() => {
                            setRecordingDone(true);
                        });
                    }, 1000);
                }
            }

            const isHighQualityRecordingActive = () => {
                return isRecordingRef.current;
            };

            useImperativeHandle(ref, () => ({
                startRecording,
                stopRecording,
                isHighQualityRecordingActive
            }), [startRecording, stopRecording]);

            return (
                <div style={{display: "none"}}/>
            )
        }
    )
;

export default ModernLocalVideoRecording;